import React, { useState, useContext, useEffect, useRef } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import colors from "../../config/colors";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import UserContext from "../../context/UserContext";
import NtModalUnactiveButton from "../../components/NtModalUnactiveButton";
import NtModalActiveButton from "../../components/NtModalActiveButton";
import NtModalInputField from "../../components/NtModalInputField";
import NtModalTitle from "../../components/NtModalTitle";
import NtModalSubtitle from "../../components/NtModalSubtitle";
import NtModalError from "../../components/NtModalError";
import NtModal from "../../components/NtModal";
import NtModalConirmAlert from "../../components/NtModalConfirmAlert";
import NtModalIvrOptionsField from "./NtModalIvrOptionsField";
import NtModalAccountPicker from "./NtModalAccountPicker";
import NtTouchableEffect from "../../components/NtTouchableEffect";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import { useTheme } from "../../context/ThemeContext";
import Logger from "../../common/utils/Logger";
import { parseError } from "@ntconnect/framework/pbx";
import {
  createIvrMenu,
  updateIvrMenu,
  deleteIvrMenu,
  createIvrOption,
  updateIvrOption,
  deleteIvrOption,
  fetchIvrOptions,
} from "../../api/ivr";

function NtIvrMenuForm({ item, onCancel, onSave, onDelete }) {
  const context = useContext(UserContext);
  const { theme } = useTheme();
  const isEdit = !!item;
  const confirmRef = useRef();
  const greetingPickerRef = useRef();

  const [name, setName] = useState(
    item?.name || item?.ivr_menu_name || "",
  );
  const [extension, setExtension] = useState(
    item?.extension || item?.ivr_menu_extension || "",
  );
  const [description, setDescription] = useState(
    item?.description || item?.ivr_menu_description || "",
  );
  const [greeting, setGreeting] = useState(
    item?.greeting_long || item?.ivr_menu_greet_long || "",
  );
  const [timeout, setTimeoutValue] = useState(
    String(item?.timeout || item?.ivr_menu_timeout || "10"),
  );

  // Single options array — in edit mode it holds saved API records, in
  // create mode it holds in-memory drafts. Same shape either way.
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const menuId = item?.id || item?.ivr_menu_uuid;
  const domain = context.user?.domain;

  // Load existing options when editing.
  useEffect(() => {
    if (!isEdit || !menuId || !domain) return;
    let alive = true;
    (async () => {
      setLoadingOptions(true);
      const res = await fetchIvrOptions(domain, menuId, 1, 100);
      Logger("IVR Options fetch", res);
      if (alive) {
        if (res?.ok) setOptions(res.data?.data || []);
        setLoadingOptions(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isEdit, menuId, domain]);

  const isRequiredFields = () => name && extension;

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    const params = {
      ivr_menu_name: name,
      ivr_menu_extension: extension,
      ivr_menu_description: description || undefined,
      ivr_menu_greet_long: greeting || undefined,
      ivr_menu_timeout: timeout ? parseInt(timeout, 10) : undefined,
    };

    const response = isEdit
      ? await updateIvrMenu(domain, menuId, params)
      : await createIvrMenu(domain, params);

    Logger("IVR Menu Save", response);

    if (!response.ok) {
      setError(parseError(response));
      setLoading(false);
      return;
    }

    // On create: persist any draft options after the menu is created.
    if (!isEdit && options.length > 0) {
      const newId =
        response.data?.id ||
        response.data?.ivr_menu_uuid ||
        response.data?.uuid;
      if (newId) {
        for (const opt of options) {
          const optRes = await createIvrOption(domain, newId, opt);
          Logger("IVR Option Create (post-menu)", optRes);
        }
      } else {
        Logger(
          "IVR Menu created but no id in response — skipping options",
          response.data,
        );
      }
    }

    setLoading(false);
    onSave?.(response.data);
  };

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    const response = await deleteIvrMenu(domain, menuId);
    Logger("IVR Menu Delete", response);

    if (response.ok) {
      onDelete?.();
    } else {
      setError(parseError(response));
    }
    setLoading(false);
  };

  const renderConfirmModal = () => (
    <NtModal ref={confirmRef}>
      <NtModalConirmAlert
        title="Delete"
        message="Are you sure you want to delete this IVR menu? This action is permanent and cannot be undone."
        onCancel={() => confirmRef.current?.dismiss()}
        onAccept={() => {
          confirmRef.current?.dismiss();
          handleDelete();
        }}
      />
    </NtModal>
  );

  // Trailing button on the Greeting input — opens the account picker locked
  // to the Recording tab so the user can pick a recording instead of typing
  // a path manually.
  const renderGreetingPickAction = () => (
    <NtTouchableEffect
      style={{
        height: 32,
        paddingHorizontal: 10,
        borderRadius: theme.radius,
        backgroundColor: theme.brand,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      }}
      onPress={() => greetingPickerRef.current?.show()}
    >
      <NtMaterialIcon name="microphone" color="#ffffff" size={18} />
    </NtTouchableEffect>
  );

  const renderGreetingPickerModal = () => (
    <NtModal ref={greetingPickerRef}>
      <NtModalAccountPicker
        defaultOpenTab="recording"
        removeOptions={["extension", "ringgroup", "voicemail", "other"]}
        onCancel={() => greetingPickerRef.current?.dismiss()}
        onPress={(selection) => {
          // The recording filename is the typical "long greeting" value for
          // FreeSWITCH IVR menus.
          const filename =
            selection?.item?.recording_filename ||
            selection?.item?.recording_name ||
            selection?.value ||
            "";
          setGreeting(filename);
          greetingPickerRef.current?.dismiss();
        }}
      />
    </NtModal>
  );

  return (
    <View style={styles.container}>
      <NtModalTitle>
        {isEdit ? "Update IVR Menu" : "Add new IVR Menu"}
      </NtModalTitle>
      <NtModalSubtitle style={[{ marginTop: 4 }]}>
        {isEdit
          ? "Update or remove this IVR menu."
          : "Define a new IVR entry point that callers can interact with."}
      </NtModalSubtitle>

      <ScrollView
        scrollEnabled={true}
        contentContainerStyle={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <NtModalInputField
          containerStyle={{ marginTop: 25 }}
          title={"Name *"}
          placeholder={"i.e Guest Main Menu"}
          value={name}
          setValue={(text) => setName(text)}
        />

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Extension *"}
          placeholder={"IVR Extension"}
          value={extension}
          setValue={(text) => setExtension(text)}
        />

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Description"}
          placeholder={"Description"}
          value={description}
          setValue={(text) => setDescription(text)}
        />

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Greeting"}
          placeholder={"Recording filename or say:<text>"}
          value={greeting}
          setValue={(text) => setGreeting(text)}
          renderAction={renderGreetingPickAction()}
          footer={
            'Use say:<text> to speak text aloud (e.g. "say:Welcome to Voxcraft"), or tap the music button to pick an uploaded recording.'
          }
        />

        <NtModalInputField
          containerStyle={{ marginTop: 15 }}
          title={"Timeout (Seconds)"}
          placeholder={"10"}
          value={timeout}
          setValue={(text) => setTimeoutValue(text)}
        />

        <NtModalIvrOptionsField
          title={"Options"}
          containerStyle={{ marginTop: 15 }}
          options={options}
          loading={loadingOptions}
          onCreate={(item) => Logger("IVR option created", item)}
          onDelete={(item) => Logger("IVR option deleted", item)}
          handleCreateRequest={async (params) => {
            // Edit mode → call API. Create mode → just buffer locally.
            if (isEdit) {
              const res = await createIvrOption(domain, menuId, params);
              Logger("IVR Option create", res);
              if (res.ok) setOptions((prev) => [...prev, res.data]);
              return res;
            }
            // Reject duplicate digits in draft mode.
            if (
              options?.find(
                (o) => o.ivr_menu_option_digits === params.ivr_menu_option_digits,
              )
            ) {
              return { ok: false, data: { error: "Digit already used" } };
            }
            setOptions((prev) => [...prev, params]);
            return { ok: true };
          }}
          handleDeleteRequest={async (item) => {
            const optId = item?.id || item?.ivr_menu_option_uuid;
            // Saved option → DELETE. Draft option → drop locally.
            if (isEdit && optId) {
              const res = await deleteIvrOption(domain, menuId, optId);
              Logger("IVR Option delete", res);
              if (res.ok) {
                setOptions((prev) =>
                  prev.filter(
                    (o) => (o.id || o.ivr_menu_option_uuid) !== optId,
                  ),
                );
              }
              return res;
            }
            setOptions((prev) =>
              prev.filter(
                (o) => o.ivr_menu_option_digits !== item.ivr_menu_option_digits,
              ),
            );
            return { ok: true };
          }}
          handleUpdateRequest={async (originalItem, params) => {
            const optId =
              originalItem?.id || originalItem?.ivr_menu_option_uuid;
            // Saved option → PATCH. Draft option → replace locally.
            if (isEdit && optId) {
              const res = await updateIvrOption(domain, menuId, optId, params);
              Logger("IVR Option update", res);
              if (res.ok) {
                setOptions((prev) =>
                  prev.map((o) =>
                    (o.id || o.ivr_menu_option_uuid) === optId
                      ? { ...o, ...res.data }
                      : o,
                  ),
                );
              }
              return res;
            }
            // Draft mode: reject if the new digit collides with another row.
            const collision = options?.find(
              (o) =>
                o.ivr_menu_option_digits === params.ivr_menu_option_digits &&
                o !== originalItem,
            );
            if (collision) {
              return { ok: false, data: { error: "Digit already used" } };
            }
            setOptions((prev) =>
              prev.map((o) => (o === originalItem ? { ...o, ...params } : o)),
            );
            return { ok: true };
          }}
        />

        <NtModalError error={error} containerStyle={{ marginTop: 6 }} />
      </ScrollView>

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 25,
        }}
      >
        {isEdit ? (
          <NtModalActiveButton
            containerStyle={{ backgroundColor: theme.red }}
            title={"Delete"}
            icon={"delete"}
            onPress={() => confirmRef.current?.show()}
            enabled={true}
          />
        ) : (
          <View />
        )}

        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {loading && (
            <NtActivityIndicator
              color={colors.blue}
              size="small"
              containerStyle={{ marginRight: 25 }}
            />
          )}

          <NtModalUnactiveButton title={"Cancel"} onPress={onCancel} />

          <NtModalActiveButton
            containerStyle={{ marginLeft: 15 }}
            title={isEdit ? "Update" : "Create"}
            onPress={() => {
              if (isRequiredFields()) handleSave();
            }}
            enabled={isRequiredFields()}
          />
        </View>
      </View>

      {renderConfirmModal()}
      {renderGreetingPickerModal()}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minWidth: 500,
    maxWidth: 600,
    padding: 15,
  },
});

export default NtIvrMenuForm;
